import { usesDesignV2 } from './features/design-v2/rollout';
import { setBonesBalance } from './api/bones';
import { useCallback, useEffect, useMemo, useReducer, useState, useRef } from 'react';
import { load, db, refreshTable, CURRENT_USER_ID } from './api/core';
import { hasActiveRuns, refreshRuns as refreshRunLists } from './api/runs';
import { refreshAssets as refreshAssetLists } from './api/library';
import * as store from './store/ui';
import { onChange, resolve, start } from './lib/router';
import './lib/routes';
import type { PageDefinition } from './lib/types';
import { bindGlobal } from './features/page-events';
import { AppLayout } from './layouts/AppLayout';
import { AppLoader } from './components/AppLoader';
import { PageView, type ViewSnapshot } from './components/PageView';

type RunHistoryWindow = Window & { BNBBrowserRunHistory?: { recover(): Promise<number> } };

export default function App() {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const previous = useRef<ViewSnapshot>({ path: '', scroll: 0, filter: '', caret: null });
  const [error, setError] = useState('');
  const [persistenceIssue, setPersistenceIssue] = useState('');
  const [homeIssue, setHomeIssue] = useState('');
  const [revision, refresh] = useReducer(value => value + 1, 0);
  const rerender = useCallback(() => refresh(), []);
  useEffect(() => {
    let active = true;
    // Preserve bookmarks from the prototype and path-style links.
    const path = location.pathname.replace(/^\/02-prototype/, '').replace(/\/$/, '');
    if (path && path !== '/index.html' && !location.hash) history.replaceState(null, '', '/#' + path + location.search);
    (async () => {
      const session = await fetch('/api/session');
      if (session.status === 401) {
        location.replace('/login?next=' + encodeURIComponent('/' + location.hash));
        return false;
      }
      if (!session.ok) throw new Error('Backend unavailable. Start the project with npm run dev.');
      setBonesBalance((await session.json()).bonesBalance);
      await load();
      await store.initializeUser(CURRENT_USER_ID, { favorites: db.favorites.map(({ entity_type, entity_id }) => ({ entity_type, entity_id })), recents: db.recents.map(({ entity_type, entity_id, at }) => ({ entity_type, entity_id, at })) });
      return true;
    })().then(ready => {
      if (!ready) return;
      if (!active) return;
      setStatus('ready');
    }).catch(cause => { if (active) { setError(String(cause.message || cause)); setStatus('error'); } });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const failed = (event: Event) => {
      const detail = String((event as CustomEvent).detail || 'Data was not saved');
      if (detail.startsWith('Could not save Home ')) setHomeIssue(detail);
      else setPersistenceIssue(detail);
    };
    const homeSaved = () => setHomeIssue('');
    const updated = () => refresh();
    const loadFailed = (event: Event) => setPersistenceIssue(String((event as CustomEvent).detail || 'Data could not be loaded'));
    window.addEventListener('cf:persistence-error', failed);
    window.addEventListener('cf:home-saved', homeSaved);
    window.addEventListener('cf:data-updated', updated);
    window.addEventListener('cf:data-error', loadFailed);
    return () => { window.removeEventListener('cf:persistence-error', failed); window.removeEventListener('cf:home-saved', homeSaved); window.removeEventListener('cf:data-updated', updated); window.removeEventListener('cf:data-error', loadFailed); };
  }, [refresh]);
  useEffect(() => {
    const stop = start();
    const unsubscribe = onChange(rerender);
    const unbind = bindGlobal(rerender);
    return () => { stop(); unsubscribe(); unbind(); };
  }, [rerender]);
  useEffect(() => {
    if (status !== 'ready') return;
    const reloadHome = () => {
      if (document.visibilityState === 'hidden') return;
      void store.refreshHome().then(rerender).catch(error => setPersistenceIssue(error.message));
    };
    window.addEventListener('focus', reloadHome);
    return () => window.removeEventListener('focus', reloadHome);
  }, [status, rerender]);
  const route = useMemo(() => resolve(), [revision, status]);
  const inCanvas = /^\/projects\/[^/]+\/flows\/[^/]+$/.test(route.path);
  const assetView = route.path === '/assets' || /^\/projects\/[^/]+$/.test(route.path) && (!route.query.tab || route.query.tab === 'overview' || route.query.tab === 'assets');
  const projectsV2 = route.path === '/projects' && usesDesignV2(route);
  const runView = projectsV2 || route.path === '/' || /^\/(?:runs|executions)(?:\/|$)/.test(route.path) ||
    /^\/admin\/(?:runs|failed)(?:\/|$)/.test(route.path) ||
    /^\/projects\/[^/]+$/.test(route.path) && route.query.tab !== 'settings';
  useEffect(() => {
    if (status !== 'ready' || !assetView) return;
    let lastRefresh = 0;
    const refreshAssets = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastRefresh < 1000) return;
      lastRefresh = Date.now();
      void refreshAssetLists().catch((): void => undefined);
    };
    refreshAssets();
    window.addEventListener('focus', refreshAssets);
    document.addEventListener('visibilitychange', refreshAssets);
    return () => { window.removeEventListener('focus', refreshAssets); document.removeEventListener('visibilitychange', refreshAssets); };
  }, [status, assetView, route.path, route.query.tab]);
  useEffect(() => {
    if (status !== 'ready' || !runView) return;
    let lastRefresh = 0;
    const refreshRuns = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastRefresh < 1000) return;
      lastRefresh = Date.now();
      void refreshRunLists().catch((): void => undefined);
    };
    refreshRuns();
    const poll = window.setInterval(() => {
      if (hasActiveRuns() || projectsV2 && db.run_stats.some(row => ['running','queued'].includes(row.status) && row.runs > 0)) refreshRuns();
    }, 5000);
    window.addEventListener('focus', refreshRuns);
    document.addEventListener('visibilitychange', refreshRuns);
    return () => { clearInterval(poll); window.removeEventListener('focus', refreshRuns); document.removeEventListener('visibilitychange', refreshRuns); };
  }, [status, runView, route.path]);
  useEffect(() => {
    if (status !== 'ready' || inCanvas) return;
    let active = true, recovering = false;
    const recover = async () => {
      if (recovering || document.visibilityState === 'hidden') return;
      recovering = true;
      try {
        const saved = await (window as RunHistoryWindow).BNBBrowserRunHistory?.recover();
        if (active && saved) await Promise.all([refreshRunLists(), refreshAssetLists()]);
      } catch { /* The saved outcome stays in localStorage for the next retry. */ }
      finally { recovering = false; }
    };
    void recover();
    const timer = window.setInterval(() => { void recover(); }, 10000);
    window.addEventListener('focus', recover);
    document.addEventListener('visibilitychange', recover);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', recover); document.removeEventListener('visibilitychange', recover); };
  }, [status, inCanvas]);
  const context = useMemo(() => ({ params: route.params, query: route.query, path: route.path, workspaceId: store.get().workspaceId, rerender }), [route, rerender]);
  const page = useMemo<PageDefinition | null>(() => {
    if (status !== 'ready') return null;
    try { return route.handler.page(context); }
    catch (cause) { console.error(cause); return { title: 'Error', content: <div className="page"><div className="banner error">Page failed to render: {String(cause.message)}</div></div> }; }
  }, [status, route, context]);
  useEffect(() => { document.documentElement.dataset.theme = store.get().theme; }, [revision, status]);
  if (status === 'loading') return <AppLoader />;
  if (status === 'error') return <div className="empty" role="alert" style={{ paddingTop: '25vh' }}>Could not load data: {error}<br /><button className="btn" onClick={() => location.reload()}>Retry</button></div>;
  if (!page) return null;
  // Table refreshes rerender the page, but must not remount a running Canvas iframe.
  return <AppLayout layout={route.handler.layout} nav={route.query.mode === 'app' ? 'use-as-app' : route.handler.nav} page={page}><PageView key={route.path} page={page} context={context} previous={previous} notice={(persistenceIssue || homeIssue) && <div className="banner error persistence-notice" role="alert"><span>{persistenceIssue ? `Changes were not saved: ${persistenceIssue}` : homeIssue}</span><button className="btn" aria-label="Dismiss notification" onClick={() => { setPersistenceIssue(''); setHomeIssue(''); }}>×</button></div>} /></AppLayout>;
}
